import { Controller } from "@hotwired/stimulus"

// Drives the press-and-hold "commit" button on the commitment onboarding step.
// While the user holds, a fixed-position circle expands out from the button
// until it fills the entire screen. Releasing early smoothly shrinks it back.
// Once full, the committed overlay fades in, haptics fire, and the committed
// event advances onboarding while the overlay fades away over the next step.
export default class extends Controller {
    static targets = ["button", "fill", "caption", "committed"]
    static values = {
        duration: { type: Number, default: 1800 }
    }

    connect() {
        this.committed = false
        this.holdTimeout = null
        this.advanceTimeout = null
        this.fadeTimeout = null
        this.hideFillTimeout = null
        this.hapticTimeouts = []
        this.captionText = this.hasCaptionTarget ? this.captionTarget.textContent : ""

        // Keep direct references: the overlays get reparented to <body> on
        // completion so they outlive the step swap, which breaks target lookup.
        this.fill = this.fillTarget
        this.committedOverlay = this.committedTarget
        this.overlaysMoved = false
    }

    disconnect() {
        this.clearTimers()

        if (this.overlaysMoved) {
            this.fill.remove()
            this.committedOverlay.remove()
            this.overlaysMoved = false
        }
    }

    preventContextMenu(event) {
        event.preventDefault()
    }

    start(event) {
        if (this.committed || this.holdTimeout) return

        event.preventDefault()

        if (event.pointerId !== undefined && this.buttonTarget.setPointerCapture) {
            try {
                this.buttonTarget.setPointerCapture(event.pointerId)
            } catch (_error) {
                // Ignore browsers that reject pointer capture; the hold still works.
            }
        }

        if (this.hideFillTimeout) {
            clearTimeout(this.hideFillTimeout)
            this.hideFillTimeout = null
        }

        this.positionFillOverButton()
        this.fill.classList.remove("hidden")
        this.fill.getBoundingClientRect() // Force reflow so the scale-up animates from zero

        this.fill.style.transition = `transform ${this.durationValue}ms cubic-bezier(0.4, 0, 0.6, 1)`
        this.fill.style.transform = `scale(${this.targetScale})`
        this.buttonTarget.style.transform = "scale(1.12)"

        if (this.hasCaptionTarget) this.captionTarget.textContent = "Keep holding..."

        this.scheduleHaptics()
        this.holdTimeout = setTimeout(() => this.complete(), this.durationValue)
    }

    reset(_event) {
        if (this.committed) return

        this.clearHoldTimers()

        this.buttonTarget.style.transform = ""
        if (this.hasCaptionTarget) this.captionTarget.textContent = this.captionText

        if (this.fill.classList.contains("hidden")) return

        this.fill.style.transition = "transform 300ms ease-out"
        this.fill.style.transform = "scale(0)"
        this.hideFillTimeout = setTimeout(() => {
            this.fill.classList.add("hidden")
            this.hideFillTimeout = null
        }, 300)
    }

    complete() {
        if (this.committed) return

        this.committed = true
        this.clearHoldTimers()

        this.fill.style.transform = `scale(${this.targetScale})`
        this.buttonTarget.style.transform = ""

        // Reparent the overlays to <body> so they keep covering the screen
        // while onboarding hides this step and shows the next one underneath.
        document.body.appendChild(this.fill)
        document.body.appendChild(this.committedOverlay)
        this.overlaysMoved = true

        this.committedOverlay.classList.remove("hidden")
        this.committedOverlay.classList.add("flex")
        requestAnimationFrame(() => this.committedOverlay.classList.remove("opacity-0"))

        this.fireCommitHaptics()

        this.advanceTimeout = setTimeout(() => {
            this.dispatch("committed")
            this.fadeOutOverlays()
        }, 1100)
    }

    fadeOutOverlays() {
        this.fill.style.transition = "opacity 500ms ease-out"
        this.fill.style.opacity = "0"
        this.committedOverlay.classList.add("opacity-0")

        this.fadeTimeout = setTimeout(() => {
            this.fill.classList.add("hidden")
            this.committedOverlay.classList.add("hidden")
            this.committedOverlay.classList.remove("flex")

            // Put the overlays back inside the step so the DOM stays tidy
            // for Turbo snapshots and repeat visits.
            this.element.appendChild(this.fill)
            this.element.appendChild(this.committedOverlay)
            this.overlaysMoved = false

            // Clear the committed flag and reset visual state so returning to
            // this step (e.g. via back navigation) leaves a live button rather
            // than a permanently-committed dead control.
            this.resetCommitState()
        }, 500)
    }

    // Restores the button to its pristine, pressable state after a completed
    // commit so the interaction works again if the user comes back.
    resetCommitState() {
        this.committed = false

        this.fill.style.transition = "none"
        this.fill.style.transform = "scale(0)"
        this.fill.style.opacity = ""
        this.fill.classList.add("hidden")

        this.buttonTarget.style.transform = ""
        if (this.hasCaptionTarget) this.captionTarget.textContent = this.captionText
    }

    // Pins the fill circle over the button and computes the scale needed for
    // it to cover the farthest corner of the viewport once fully expanded.
    positionFillOverButton() {
        const rect = this.buttonTarget.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        const corners = [
            [0, 0],
            [window.innerWidth, 0],
            [0, window.innerHeight],
            [window.innerWidth, window.innerHeight]
        ]
        const radius = Math.max(...corners.map(([x, y]) => Math.hypot(x - centerX, y - centerY)))
        this.targetScale = ((radius * 2) / rect.width) * 1.05

        const style = this.fill.style
        style.left = `${rect.left}px`
        style.top = `${rect.top}px`
        style.width = `${rect.width}px`
        style.height = `${rect.height}px`
        style.opacity = ""
        style.transition = "none"
        style.transform = "scale(0)"
    }

    // Builds a "charging up" haptic ramp: gentle ticks that accelerate as the
    // ring fills, escalating from light to medium as the user nears the commit
    // so the hold feels like tension building toward a release.
    scheduleHaptics() {
        this.haptic("medium")

        const ticks = this.hapticSchedule()
        this.hapticTimeouts = ticks.map(({ time, level }) => {
            return setTimeout(() => this.haptic(level), time)
        })
    }

    // Returns { time, level } ticks spread across the hold. The gap between
    // ticks shrinks over time (accelerating cadence) and the final stretch
    // switches to medium taps for a stronger "almost there" feeling.
    hapticSchedule() {
        const ticks = []
        const end = this.durationValue - 90
        let time = 0
        let gap = 230

        while (time + gap < end) {
            time += gap
            const progress = time / this.durationValue
            const level = progress >= 0.75 ? "medium" : "light"
            ticks.push({ time, level })
            gap = Math.max(70, gap * 0.86)
        }

        return ticks
    }

    // Fires a quick escalating double-tap the moment the commit lands so the
    // release feels like a satisfying snap rather than a single buzz.
    fireCommitHaptics() {
        this.haptic("medium")
        this.hapticTimeouts.push(setTimeout(() => this.haptic("medium"), 90))
    }

    haptic(level) {
        if (window.triggerHaptic) window.triggerHaptic(level)
    }

    clearHoldTimers() {
        if (this.holdTimeout) {
            clearTimeout(this.holdTimeout)
            this.holdTimeout = null
        }

        this.hapticTimeouts.forEach((timeout) => clearTimeout(timeout))
        this.hapticTimeouts = []
    }

    clearTimers() {
        this.clearHoldTimers()

        if (this.advanceTimeout) {
            clearTimeout(this.advanceTimeout)
            this.advanceTimeout = null
        }

        if (this.fadeTimeout) {
            clearTimeout(this.fadeTimeout)
            this.fadeTimeout = null
        }

        if (this.hideFillTimeout) {
            clearTimeout(this.hideFillTimeout)
            this.hideFillTimeout = null
        }
    }
}
